import {skipToken} from '@reduxjs/toolkit/query';
import {Button} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {Lightbox, useLightbox} from 'platform/lightbox';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetPreviewFromSettingsQuery} from '@dms/api/core';
import {useGetWatermarkDetailQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

const LIGHTBOX_ID = 'campaign-watermark-preset-preview';

interface WatermarkPresetPreviewProps extends RequiredTestIdProps {
  watermarkId: string | null;
}

export function WatermarkPresetPreview(props: WatermarkPresetPreviewProps) {
  const {data: watermark} = useGetWatermarkDetailQuery(
    isNotNil(props.watermarkId) ? {watermarkId: props.watermarkId} : skipToken
  );

  const isWatermarkIdValid = isNotNil(props.watermarkId);

  const {data, isLoading, isFetching, isError} = useGetPreviewFromSettingsQuery(
    isNotNil(watermark) && isNotNil(watermark.fileId)
      ? {
          fileId: watermark.fileId,
          position: watermark.position,
          size: watermark.size,
          fitToImage: watermark.fitToImage,
        }
      : skipToken
  );

  const base64Url = data?.base64Url;

  const [lightboxControls, {onOpen}] = useLightbox(LIGHTBOX_ID);

  return (
    <VStack spacing={1} align="flex-start">
      <Text size="small" color="secondary">
        {i18n.t('entity.salesCampaign.labels.watermarkPreview')}
      </Text>
      <Button
        variant="link"
        title={i18n.t('entity.salesCampaign.actions.showWatermarkSamplePreview')}
        rightIcon="navigation/chevron_right"
        isLoading={isLoading || isFetching}
        isDisabled={isError || !isWatermarkIdValid || !base64Url}
        onClick={() => onOpen(0)}
        data-testid={suffixTestId('showPreview', props)}
      />

      <Show when={isNotNilOrEmpty(base64Url)}>
        <Lightbox
          controls={lightboxControls}
          images={[{id: LIGHTBOX_ID, url: base64Url ?? ''}]}
          data-testid={suffixTestId('lightbox', props)}
        />
      </Show>
    </VStack>
  );
}
